/** BCP-47-ish codes; extend when adding locale bundles. */
export const SUPPORTED_LOCALES = ['en'] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const FALLBACK_LOCALE: SupportedLocale = 'en';

/**
 * Temporary QA override: set to a supported locale code, then reload (or use setQaLocaleOverride).
 * Invalid values are ignored at startup.
 */
export const QA_LOCALE_STORAGE_KEY = 'platform_web_i18n_locale';

export function isSupportedLocale(value: string): value is SupportedLocale {
    return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function readQaLocaleOverride(): SupportedLocale | null {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(QA_LOCALE_STORAGE_KEY);
        if (!raw) return null;
        const trimmed = raw.trim();
        return isSupportedLocale(trimmed) ? trimmed : null;
    } catch {
        return null;
    }
}

/** Pick best supported locale from browser preferences (no localStorage). */
export function detectBrowserLocale(): SupportedLocale {
    if (typeof window === 'undefined') return FALLBACK_LOCALE;

    const tags: string[] = [];
    const nav = navigator as Navigator & { userLanguage?: string };
    if (nav.languages && nav.languages.length > 0) {
        for (let i = 0; i < nav.languages.length; i += 1) {
            tags.push(nav.languages[i]);
        }
    }
    if (nav.language) tags.push(nav.language);
    if (nav.userLanguage) tags.push(nav.userLanguage);

    for (let j = 0; j < tags.length; j += 1) {
        const tag = tags[j].toLowerCase();
        const base = tag.split('-')[0];
        for (let k = 0; k < SUPPORTED_LOCALES.length; k += 1) {
            const supported = SUPPORTED_LOCALES[k];
            if (tag === supported || base === supported) return supported;
        }
    }

    return FALLBACK_LOCALE;
}

/** QA localStorage wins; otherwise browser languages. */
export function resolveInitialLocale(): SupportedLocale {
    const stored = readQaLocaleOverride();
    if (stored) return stored;
    return detectBrowserLocale();
}
